import { useEffect, useState, useCallback } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";

export default function AdminGate() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [newCode, setNewCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/gate/admin").then((r) => {
      setData(r.data);
      setEnabled(r.data.enabled);
    }).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Skeleton className="h-40 w-full" />;

  const save = async () => {
    if (newCode && newCode !== confirmCode) {
      toast.error(t("gateMismatch"));
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.put("/gate/admin", {
        enabled, new_code: newCode || null });
      if (res.gate_token) localStorage.setItem("apha_gate", res.gate_token);
      toast.success(res.code_rotated ? t("gateRotated") : t("saveSettings") + " ✓");
      setNewCode("");
      setConfirmCode("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="apha-card p-5 space-y-4 max-w-2xl" data-testid="admin-gate-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold flex items-center gap-2">
          <KeyRound className="h-4 w-4" />{t("accessGate")}
        </h2>
        <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${
          data.configured && data.enabled ? "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]"
            : "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]"}`} data-testid="gate-status-badge">
          {data.configured && data.enabled ? t("gateActiveBadge") : t("gateOffBadge")}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{t("gateAdminHint")}</p>
      {data.configured && (
        <div className="text-xs text-muted-foreground" data-testid="gate-meta">
          v{data.version}
          {data.rotated_at && ` · ${t("gateLastRotated")}: ${new Date(data.rotated_at).toLocaleString()}`}
          {data.updated_by_name && ` · ${data.updated_by_name}`}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="gate-enabled-switch" />
        <span className="text-sm">{t("gateEnabledLabel")}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t("gateRotateLabel")}</Label>
          <Input type="password" data-testid="gate-new-code-input" autoComplete="off"
            placeholder={data.configured ? "••••••••" : t("gateCodePlaceholder")}
            value={newCode} onChange={(e) => setNewCode(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("gateConfirmLabel")}</Label>
          <Input type="password" data-testid="gate-confirm-code-input" autoComplete="off"
            value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} />
        </div>
      </div>
      <div className="text-xs bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] rounded px-2.5 py-1.5">
        {t("gateRotateWarning")}
      </div>
      <Button data-testid="gate-save-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
        disabled={busy || (!data.configured && !newCode)} onClick={save}>
        {t("saveSettings")}
      </Button>
    </div>
  );
}
